import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Bot, Filter } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface BotActivityChartProps {
  days: number
  section?: string
}

const BOT_TYPE_COLORS: Record<string, string> = {
  search_engine: '#10b981', // Green for search engines
  ai: '#8b5cf6',           // Purple for AI bots
  social: '#f59e0b',       // Amber for social
  monitoring: '#6366f1',   // Indigo for monitoring
  other: '#94a3b8',        // Slate for others
}

const BOT_TYPE_LABELS: Record<string, string> = {
  search_engine: 'Buscadores',
  ai: 'Bots IA',
  social: 'Redes Sociales',
  monitoring: 'Monitoreo',
  other: 'Otros',
}

export default function BotActivityChart({ days, section }: BotActivityChartProps) {
  const [botTypeFilter, setBotTypeFilter] = useState<string | null>(null)
  const [botIdFilter, setBotIdFilter] = useState<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['seo-bot-activity', days, botTypeFilter, botIdFilter, section],
    queryFn: () => seoAnalytics.getBotActivity({
      days,
      bot_type: botIdFilter ? undefined : (botTypeFilter || undefined),
      bot_id: botIdFilter || undefined,
      section: section || undefined
    }),
  })

  // Get list of all bots for individual filter
  const botListQuery = useQuery({
    queryKey: ['seo-bot-list'],
    queryFn: () => seoAnalytics.getBotList(),
  })

  // Get unique bot types from summary for filter
  const summaryQuery = useQuery({
    queryKey: ['seo-bot-summary', days, section],
    queryFn: () => seoAnalytics.getBotSummary(days, section),
  })

  const botTypes = summaryQuery.data?.data?.by_bot
    ? [...new Set(summaryQuery.data.data.by_bot.map((b: any) => b.bot_type))]
    : []

  const botList = botListQuery.data?.data || []

  // When selecting individual bot, clear type filter
  const handleBotIdChange = (value: string) => {
    if (value) {
      setBotIdFilter(parseInt(value))
      setBotTypeFilter(null)
    } else {
      setBotIdFilter(null)
    }
  }

  // When selecting type, clear individual bot filter
  const handleBotTypeChange = (value: string) => {
    setBotTypeFilter(value || null)
    setBotIdFilter(null)
  }

  // Transform data for chart - group by date and bot type
  const chartData = (() => {
    if (!data?.data || !Array.isArray(data.data)) return []

    // Group by date
    const byDate: Record<string, Record<string, number>> = {}
    data.data.forEach((item: any) => {
      if (!byDate[item.date]) {
        byDate[item.date] = {}
      }
      const botType = item.bot_type || 'other'
      byDate[item.date][botType] = (byDate[item.date][botType] || 0) + item.hits
    })

    // Convert to array format for Recharts
    return Object.entries(byDate)
      .map(([date, types]) => ({
        date,
        dateFormatted: new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        ...types,
      }))
      .sort((a, b) => a.date.localeCompare(b.date))
  })()

  // Get all bot types present in data
  const presentBotTypes = chartData.length > 0
    ? [...new Set(chartData.flatMap(d => Object.keys(d).filter(k => k !== 'date' && k !== 'dateFormatted')))]
    : []

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30">
            <Bot className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Actividad de Bots</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Rastreos por dia y tipo de bot
            </p>
          </div>
        </div>

        {/* Bot filters */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          {/* Individual bot filter */}
          <select
            value={botIdFilter?.toString() || ''}
            onChange={(e) => handleBotIdChange(e.target.value)}
            className="input input-sm w-36"
          >
            <option value="">Todos los bots</option>
            {botList.map((bot: any) => (
              <option key={bot.id} value={bot.id.toString()}>
                {bot.bot_name}
              </option>
            ))}
          </select>
          {/* Bot type filter */}
          <select
            value={botTypeFilter || ''}
            onChange={(e) => handleBotTypeChange(e.target.value)}
            className="input input-sm w-36"
            disabled={!!botIdFilter}
          >
            <option value="">Todos tipos</option>
            {botTypes.map((type) => (
              <option key={type as string} value={type as string}>
                {BOT_TYPE_LABELS[type as string] || type}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card-body">
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-pulse text-slate-400">Cargando datos...</div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-80 flex flex-col items-center justify-center text-slate-400">
            <Bot className="w-12 h-12 mb-4 opacity-50" />
            <p>No hay datos de bots para el periodo seleccionado</p>
            <p className="text-sm mt-1">Ejecuta una sincronizacion para obtener datos</p>
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  {presentBotTypes.map((type) => (
                    <linearGradient key={type} id={`gradient-${type}`} x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={BOT_TYPE_COLORS[type] || '#94a3b8'}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={BOT_TYPE_COLORS[type] || '#94a3b8'}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="dateFormatted"
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                />
                <Legend
                  verticalAlign="top"
                  height={36}
                  formatter={(value: string) => BOT_TYPE_LABELS[value as keyof typeof BOT_TYPE_LABELS] || value}
                />
                {presentBotTypes.map((type) => (
                  <Area
                    key={type}
                    type="monotone"
                    dataKey={type}
                    stroke={BOT_TYPE_COLORS[type] || '#94a3b8'}
                    strokeWidth={2}
                    fill={`url(#gradient-${type})`}
                    stackId="1"
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}
